import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Deep partial type - makes all nested properties optional
 */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * Generic translation input - either a flat dictionary or deep partial nested object
 */
export type TranslationInput<T> = DeepPartial<T> | { [key: string]: string };

/**
 * Generic translation service base class
 * Provides a centralized way to manage translations with hierarchical precedence:
 * 1. Global config translations (highest precedence)
 * 2. Local component-level translations
 * 3. Default translations (lowest precedence)
 * 
 * @example
 * // Define your translation interface
 * interface MyTranslations {
 *   buttons: {
 *     save: string;
 *     cancel: string;
 *   };
 *   messages: {
 *     success: string;
 *   };
 * }
 * 
 * // Create a service extending this base
 * @Injectable({ providedIn: 'root' })
 * class MyTranslationService extends GenericTranslationService<MyTranslations> {
 *   constructor() {
 *     super({
 *       buttons: { save: 'Save', cancel: 'Cancel' },
 *       messages: { success: 'Success!' }
 *     });
 *   }
 * }
 */
@Injectable()
export abstract class GenericTranslationService<T extends Record<string, any>> {
  private _globalTranslations = new BehaviorSubject<DeepPartial<T>>({});
  
  /**
   * Registry of available translations by language code
   * Concrete services should populate this in their constructor
   */
  protected languageRegistry: Map<string, T> = new Map();
  
  /**
   * @param defaultTranslations Default translations (English or fallback language)
   */
  constructor(
    protected defaultTranslations: T
  ) {
    // Register default language (English)
    this.languageRegistry.set('en', defaultTranslations);
  }

  /**
   * Set global translations that apply to all component instances
   * @param translations Partial translations object or flat dictionary
   */
  setGlobalTranslations(translations: TranslationInput<T>): void {
    const normalized = this.normalizeTranslations(translations);
    this._globalTranslations.next(normalized);
  }

  /**
   * Get current global translations as observable
   */
  get globalTranslations$(): Observable<DeepPartial<T>> {
    return this._globalTranslations.asObservable();
  }

  /**
   * Get current global translations
   */
  get globalTranslations(): DeepPartial<T> {
    return this._globalTranslations.value;
  }

  /**
   * Register a language translation set
   * @param languageCode Language code (e.g., 'en', 'de', 'fr')
   * @param translations Complete translations for the language
   */
  registerLanguage(languageCode: string, translations: T): void {
    this.languageRegistry.set(languageCode.toLowerCase(), translations);
  }

  /**
   * Get translations by language code
   * @param languageCode Language code (e.g., 'en', 'de')
   * @returns Translations for the specified language, or default if not found
   */
  getTranslationsByLanguage(languageCode: string): T {
    const translations = this.languageRegistry.get(languageCode.toLowerCase());
    
    if (!translations) {
      console.warn(`Translations for language '${languageCode}' not found. Falling back to default.`);
      return this.defaultTranslations;
    }
    
    return translations;
  }

  /**
   * Get available language codes
   * @returns Array of registered language codes
   */
  getAvailableLanguages(): string[] {
    return Array.from(this.languageRegistry.keys());
  }

  /**
   * Check if a language is supported
   * @param languageCode Language code to check
   * @returns True if the language is registered
   */
  isLanguageSupported(languageCode: string): boolean {
    return this.languageRegistry.has(languageCode.toLowerCase());
  }

  /**
   * Set global translations by language code
   * This is a convenient shorthand for getTranslationsByLanguage + setGlobalTranslations
   * @param languageCode Language code (e.g., 'en', 'de')
   */
  setLanguage(languageCode: string): void {
    const translations = this.getTranslationsByLanguage(languageCode);
    this.setGlobalTranslations(translations as DeepPartial<T>);
  }

  /**
   * Get resolved translations with proper precedence
   * @param localTranslations Optional local component-level translations
   * @returns Complete translations object with all keys filled
   */
  getResolvedTranslations(localTranslations?: TranslationInput<T>): T {
    const global = this.globalTranslations;
    const local = localTranslations ? this.normalizeTranslations(localTranslations) : {};
    
    // Merge with precedence: local > global > default
    return this.deepMerge(
      this.defaultTranslations,
      global,
      local
    ) as T;
  }

  /**
   * Get a specific translation key with interpolation support
   * @param key Dot-notation key (e.g., 'buttons.save')
   * @param interpolations Optional values for template interpolation
   * @param localTranslations Optional local translations (will be resolved if provided)
   * @returns Translated and interpolated string
   */
  get(
    key: string, 
    interpolations?: { [key: string]: string | number },
    localTranslations?: TranslationInput<T>
  ): string {
    const translations = this.getResolvedTranslations(localTranslations);
    const value = this.getNestedValue(translations, key);
    
    if (typeof value !== 'string') {
      console.warn(`Translation key '${key}' not found or is not a string`);
      return key;
    }

    return interpolations ? this.interpolate(value, interpolations) : value;
  }

  /**
   * Get a specific translation key from pre-resolved translations with interpolation support
   * This is more efficient when you already have resolved translations
   * @param key Dot-notation key (e.g., 'buttons.save')
   * @param resolvedTranslations Pre-resolved translations object
   * @param interpolations Optional values for template interpolation
   * @returns Translated and interpolated string
   */
  getFromResolved(
    key: string,
    resolvedTranslations: T,
    interpolations?: { [key: string]: string | number }
  ): string {
    const value = this.getNestedValue(resolvedTranslations, key);
    
    if (typeof value !== 'string') {
      console.warn(`Translation key '${key}' not found or is not a string`);
      return key;
    }

    return interpolations ? this.interpolate(value, interpolations) : value;
  }

  /**
   * Normalize different input formats to DeepPartial<T>
   * @param input Dictionary or partial translations
   * @returns Normalized partial translations
   */
  protected normalizeTranslations(input: TranslationInput<T>): DeepPartial<T> {
    if (this.isDictionary(input)) {
      return this.convertDictionaryToTranslations(input);
    }
    return input as DeepPartial<T>;
  }

  /**
   * Check if input is a simple dictionary (all values are strings)
   */
  protected isDictionary(input: TranslationInput<T>): input is { [key: string]: string } {
    if (typeof input !== 'object' || input === null) return false;
    
    // Check if all values are strings (dictionary format)
    return Object.values(input).every(value => typeof value === 'string');
  }

  /**
   * Convert flat dictionary to nested translation structure
   * Supports dot notation keys like 'buttons.save' -> { buttons: { save: 'value' } }
   */
  protected convertDictionaryToTranslations(dict: { [key: string]: string }): DeepPartial<T> {
    const result: any = {};
    
    for (const [key, value] of Object.entries(dict)) {
      this.setNestedValue(result, key, value);
    }
    
    return result;
  }

  /**
   * Set nested value using dot notation
   * @param obj Target object
   * @param path Dot-notation path
   * @param value Value to set
   */
  protected setNestedValue(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    let current = obj;
    
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }
    
    current[keys[keys.length - 1]] = value;
  }

  /**
   * Get nested value using dot notation
   * @param obj Source object
   * @param path Dot-notation path
   * @returns Value or undefined
   */
  protected getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  /**
   * Deep merge multiple objects with precedence (later objects override earlier ones)
   */
  protected deepMerge(...objects: any[]): any {
    const result: any = {};
    
    for (const obj of objects) {
      if (!obj || typeof obj !== 'object') continue;
      
      for (const [key, value] of Object.entries(obj)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          result[key] = this.deepMerge(result[key] || {}, value);
        } else {
          result[key] = value;
        }
      }
    }
    
    return result;
  }

  /**
   * Interpolate template strings with values
   * @param template Template string with {key} placeholders
   * @param values Values to interpolate
   * @returns Interpolated string
   * @example
   * interpolate('Hello {name}!', { name: 'World' }) // 'Hello World!'
   */
  protected interpolate(template: string, values: { [key: string]: string | number }): string {
    return template.replace(/\{([^}]+)\}/g, (match, key) => {
      const value = values[key];
      return value !== undefined ? String(value) : match;
    });
  }
}
